import type { TextMessagePartComponent } from '@assistant-ui/react';
import {
  type CodeHeaderProps,
  MarkdownTextPrimitive,
  unstable_memoizeMarkdownComponents as memoizeMarkdownComponents,
  useIsMarkdownCodeBlock,
} from '@assistant-ui/react-markdown';
import { Check, Copy } from 'lucide-react';
import * as React from 'react';
import remarkGfm from 'remark-gfm';
import { cn } from '../lib/utils.js';
import {
  CITATION_TAG,
  ParcelCitationElement,
  remarkCitations,
  trimPartialCitation,
} from './assistant-citation.js';

function CodeHeader({ language, code }: CodeHeaderProps) {
  const [copied, setCopied] = React.useState(false);
  const onCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div
      data-slot="markdown-code-header"
      className="flex items-center justify-between rounded-t-md border border-b-0 border-border bg-muted px-3 py-1 text-xs text-muted-foreground"
    >
      <span>{language ?? 'code'}</span>
      <button
        type="button"
        aria-label={copied ? 'Copied' : 'Copy code'}
        onClick={onCopy}
        className="inline-flex items-center gap-1 rounded px-1 hover:text-foreground"
      >
        {copied ? (
          <Check className="size-3" aria-hidden />
        ) : (
          <Copy className="size-3" aria-hidden />
        )}
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

/** A heading in a summary is just a line of prose: same size, same weight. */
function FlatHeading({ className, ...props }: React.HTMLAttributes<HTMLElement>) {
  return <p className={cn('mb-2 leading-relaxed last:mb-0', className)} {...props} />;
}

type MarkdownComponents = NonNullable<
  React.ComponentProps<typeof MarkdownTextPrimitive>['components']
>;

/** What `memoizeMarkdownComponents` accepts, which is wider than what it returns. */
type MarkdownComponentsInput = Parameters<typeof memoizeMarkdownComponents>[0];

const baseMarkdownComponents: MarkdownComponentsInput = {
  h1: ({ className, ...props }) => (
    <h1 className={cn('mb-2 mt-4 text-base font-semibold first:mt-0', className)} {...props} />
  ),
  h2: ({ className, ...props }) => (
    <h2 className={cn('mb-2 mt-4 text-base font-semibold first:mt-0', className)} {...props} />
  ),
  h3: ({ className, ...props }) => (
    <h3 className={cn('mb-1 mt-3 text-sm font-semibold first:mt-0', className)} {...props} />
  ),
  p: ({ className, ...props }) => (
    <p className={cn('mb-3 leading-relaxed last:mb-0', className)} {...props} />
  ),
  ul: ({ className, ...props }) => (
    <ul className={cn('mb-3 list-disc pl-5 last:mb-0', className)} {...props} />
  ),
  ol: ({ className, ...props }) => (
    <ol className={cn('mb-3 list-decimal pl-5 last:mb-0', className)} {...props} />
  ),
  li: ({ className, ...props }) => <li className={cn('mb-1', className)} {...props} />,
  blockquote: ({ className, ...props }) => (
    <blockquote
      className={cn('mb-3 border-l-2 border-border pl-3 text-muted-foreground', className)}
      {...props}
    />
  ),
  a: ({ className, ...props }) => (
    <a
      className={cn('underline underline-offset-2 hover:text-foreground', className)}
      target="_blank"
      rel="noopener noreferrer"
      {...props}
    />
  ),
  table: ({ className, ...props }) => (
    <div data-slot="markdown-table-scroll" className="mb-3 overflow-x-auto last:mb-0">
      <table className={cn('w-full border-collapse text-xs', className)} {...props} />
    </div>
  ),
  th: ({ className, ...props }) => (
    <th
      className={cn('border border-border bg-muted px-2 py-1 text-left font-medium', className)}
      {...props}
    />
  ),
  td: ({ className, ...props }) => (
    <td className={cn('border border-border px-2 py-1 align-top', className)} {...props} />
  ),
  pre: ({ className, ...props }) => (
    <pre
      className={cn(
        'mb-3 overflow-x-auto rounded-b-md border border-border bg-muted/40 p-3 font-mono text-xs last:mb-0',
        className,
      )}
      {...props}
    />
  ),
  code: function Code({ className, ...props }) {
    const isCodeBlock = useIsMarkdownCodeBlock();
    return (
      <code
        className={cn(
          !isCodeBlock && 'rounded bg-muted px-1 py-0.5 font-mono text-[0.85em]',
          className,
        )}
        {...props}
      />
    );
  },
  CodeHeader,
  // Not an HTML tag, so outside the components type; `remarkCitations` emits it.
  ...({ [CITATION_TAG]: ParcelCitationElement } as MarkdownComponentsInput),
};

/**
 * Splits streamed prose into one span per word, keyed by position. A word
 * still being typed keeps its node and its finished animation; only an
 * appended one mounts, and only a mount starts the fade. Whitespace runs are
 * pieces too, so the copy reads exactly as written. Element children pass
 * through for their own component to split.
 */
function StreamWords({ children }: { children?: React.ReactNode }) {
  return (
    <>
      {React.Children.map(children, (child) =>
        typeof child !== 'string'
          ? child
          : // A split yields '' at each end, so a text child beside emphasis would
            // otherwise mount an empty span. Keyed by the ORIGINAL position, so
            // dropping one never shifts the key of a word still being typed.
            child
              .split(/(\s+)/)
              .map((piece, index) =>
                piece === '' ? null : (
                  // biome-ignore lint/suspicious/noArrayIndexKey: position IS the identity here -- a stable key is what keeps a growing word from remounting.
                  <span key={index} data-slot="stream-word">
                    {piece}
                  </span>
                ),
              ),
      )}
    </>
  );
}

/**
 * Tags that carry inline prose. `code` and `pre` are deliberately absent:
 * spans inside them wreck the formatting they exist to preserve.
 */
const STREAM_WORD_TAGS = [
  'p',
  'li',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'strong',
  'em',
  'a',
  'td',
  'th',
] as const;

type BlockComponent = React.ElementType<{ children?: React.ReactNode }>;

const withStreamWords = (config: MarkdownComponentsInput): MarkdownComponentsInput => {
  const wrapped: Record<string, BlockComponent> = {};
  for (const tag of STREAM_WORD_TAGS) {
    // Falling back to the intrinsic tag matters: `strong`, `em` and h4-h6 have
    // no override, and skipping them leaves bold text popping amid faded prose.
    const Base = (config as Record<string, BlockComponent | undefined>)[tag] ?? tag;
    wrapped[tag] = ({ children, ...props }) => (
      <Base {...props}>
        <StreamWords>{children}</StreamWords>
      </Base>
    );
  }
  return { ...config, ...wrapped } as MarkdownComponentsInput;
};

export const markdownComponents: MarkdownComponents =
  memoizeMarkdownComponents(baseMarkdownComponents);

/**
 * The same components, one span per word. Only mounted while a part streams:
 * a settled answer keeps plain text nodes, so a long thread carries no extra
 * DOM and prose stays one findable string.
 */
export const streamingMarkdownComponents: MarkdownComponents = memoizeMarkdownComponents(
  withStreamWords(baseMarkdownComponents),
);

/**
 * Markdown for a chain-of-thought summary. Emphasis still parses -- so no raw
 * asterisks leak -- but headings and bold flatten: an aside must not grow a
 * second hierarchy inside the fold.
 */
const reasoningMarkdownConfig: MarkdownComponentsInput = {
  ...baseMarkdownComponents,
  h1: FlatHeading,
  h2: FlatHeading,
  h3: FlatHeading,
  h4: FlatHeading,
  h5: FlatHeading,
  h6: FlatHeading,
  strong: ({ className, ...props }) => <span className={cn('font-normal', className)} {...props} />,
  p: ({ className, ...props }) => (
    <p className={cn('mb-2 leading-relaxed last:mb-0', className)} {...props} />
  ),
};

export const reasoningMarkdownComponents: MarkdownComponents =
  memoizeMarkdownComponents(reasoningMarkdownConfig);

export const streamingReasoningMarkdownComponents: MarkdownComponents = memoizeMarkdownComponents(
  withStreamWords(reasoningMarkdownConfig),
);

/**
 * How often the smooth reveal commits, in ms. The animator advances every
 * frame either way; this only caps how often that costs a full markdown
 * re-parse of the message -- 60fps of them is what makes a long answer stutter.
 */
export const SMOOTH_COMMIT_MS = 32;

/**
 * New blocks ease in as they stream. Scoped to `data-status=running` on
 * purpose: the settled message replaces the live overlay, remounting every
 * block, and an unscoped rule would re-fade the whole answer at the end.
 */
const BLOCK_FADE =
  '[&[data-status=running]>*]:motion-safe:animate-in [&[data-status=running]>*]:motion-safe:fade-in [&[data-status=running]>*]:motion-safe:[animation-duration:300ms]';

/** The same, one word at a time -- this is the part that reads as streaming. */
const WORD_FADE =
  '[&[data-status=running]_[data-slot=stream-word]]:motion-safe:animate-in [&[data-status=running]_[data-slot=stream-word]]:motion-safe:fade-in [&[data-status=running]_[data-slot=stream-word]]:motion-safe:[animation-duration:300ms]';

export const STREAM_FADE_IN = `${BLOCK_FADE} ${WORD_FADE}`;

const MARKDOWN_PLUGINS = [remarkGfm, remarkCitations];
const LIVE_MARKDOWN_PLUGINS = [remarkGfm, [remarkCitations, { live: true }]] as const;

/**
 * Markdown for assistant text parts; user text stays plain elsewhere. The
 * streaming caret is a pseudo-element on the last block so it sits at the end
 * of the text rather than on a line of its own below it.
 */
export const ParcelMarkdownText: TextMessagePartComponent = ({ status }) => (
  <MarkdownTextPrimitive
    smooth={{ minCommitMs: SMOOTH_COMMIT_MS }}
    containerProps={{ 'data-slot': 'assistant-markdown' } as React.HTMLAttributes<HTMLDivElement>}
    className={cn(
      "break-words text-sm leading-relaxed [&[data-status=running]>:last-child]:after:ml-0.5 [&[data-status=running]>:last-child]:after:content-['▍'] [&[data-status=running]>:last-child]:after:motion-safe:animate-pulse",
      STREAM_FADE_IN,
    )}
    remarkPlugins={
      status?.type === 'running'
        ? (LIVE_MARKDOWN_PLUGINS as unknown as typeof MARKDOWN_PLUGINS)
        : MARKDOWN_PLUGINS
    }
    preprocess={status?.type === 'running' ? trimPartialCitation : undefined}
    components={status?.type === 'running' ? streamingMarkdownComponents : markdownComponents}
  />
);
